import { Component } from '@theme/component';

/**
 * Tabbed content block (`blocks/tabs.liquid` + `blocks/_tab.liquid`).
 *
 * Structure contract: each `_tab` child block server-renders an interleaved
 * `<button class="tabs__tab">` + `<div class="tabs__panel">` sibling pair
 * (the wrapper block can't enumerate its children in Liquid, so there is no
 * server-rendered tablist containing the buttons — CSS `order` lays the
 * buttons out as the tab bar). The empty `.tabs__list` element adopts the
 * buttons in the accessibility tree via `aria-owns`, set here once the ids
 * are known.
 *
 * All state writes (`aria-selected`, `tabindex`, `hidden`, `aria-owns`,
 * `data-initialized`) are applied idempotently in #apply and re-asserted by a
 * MutationObserver: a section morph resets attributes to their server values
 * (which carry no active-tab state), so the component self-heals from the
 * active id it keeps in memory.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement[]} [tabButtons]
 * @property {HTMLElement[]} [tabPanels]
 *
 * @extends {Component<Refs>}
 */
class TabsComponent extends Component {
  /** @type {string | null} The id of the active tab button. */
  #activeTabId = null;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#activateFromHash({ scroll: true });
    this.#apply();

    this.#observer = new MutationObserver(this.#scheduleApply);
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-selected', 'data-initialized'],
    });

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    window.addEventListener('hashchange', this.#onHashChange, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  // Both getters filter on isConnected: the base Component class doesn't
  // refresh refs on pure node removals, so refs can briefly hold detached
  // elements after a tab is removed.

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return (this.refs.tabButtons ?? []).filter((tab) => tab.isConnected);
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return (this.refs.tabPanels ?? []).filter((panel) => panel.isConnected);
  }

  /**
   * Activates the clicked tab. Wired declaratively via `on:click="/select"`.
   *
   * @param {Event} event
   */
  select(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.tabs__tab');
    if (!(button instanceof HTMLElement) || !this.#tabs.includes(/** @type {HTMLButtonElement} */ (button))) return;

    this.#activate(button.id);
  }

  /**
   * Roving-focus keyboard support per the ARIA tabs pattern. Wired via
   * `on:keydown="/handleKeydown"` on each tab button.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const tabs = this.#tabs;
    const current = tabs.findIndex((tab) => tab === event.target);
    if (current === -1) return;

    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const tab = tabs[next];
    if (!tab) return;

    this.#activate(tab.id);
    tab.focus();
  }

  /**
   * In the theme editor, reveal the tab whose panel contains the selected
   * block (the `_tab` block itself, or any block nested inside it).
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const target = event.target;
    const panel = this.#panels.find((candidate) => candidate.contains(target));
    if (!panel) return;

    const tab = this.#tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
    if (tab) this.#activate(tab.id);
  };

  #onHashChange = () => {
    if (this.#activateFromHash({ scroll: true })) this.#apply();
  };

  /**
   * If the URL hash targets an element inside one of the panels, make that
   * panel's tab active. This is what keeps forms inside tabs working:
   * Shopify forms (contact, inquiry) post back with a full page reload
   * anchored to `#<FormId>` — without this, the reload would reset to the
   * first tab and hide the form's success/error state. Also covers plain
   * anchor links pointing into a panel.
   *
   * @param {{ scroll?: boolean }} [options] Re-run the browser's anchor
   *   scroll after revealing (the native one fails while the panel is hidden).
   * @returns {boolean} Whether a matching panel was found.
   */
  #activateFromHash({ scroll = false } = {}) {
    const hash = window.location.hash.slice(1);
    if (!hash) return false;

    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(hash));
    } catch {
      target = document.getElementById(hash);
    }
    if (!target || !this.contains(target)) return false;

    const panel = this.#panels.find((candidate) => candidate === target || candidate.contains(target));
    if (!panel) return false;

    const tab = this.#tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
    if (!tab) return false;

    this.#activeTabId = tab.id;

    if (scroll) {
      requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));
    }

    return true;
  }

  /** @param {string} id */
  #activate(id) {
    if (!id) return;
    this.#activeTabId = id;
    this.#apply();
  }

  #scheduleApply = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.isConnected) this.#apply();
    });
  };

  /**
   * Applies the active-tab state to the DOM. Idempotent: every write is
   * guarded by a compare, so the self-healing MutationObserver settles
   * instead of looping.
   */
  #apply() {
    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    // Fall back to the first tab when the remembered one no longer exists
    // (e.g. the merchant removed that tab block).
    const active = tabs.find((tab) => tab.id === this.#activeTabId) ?? tabs[0];
    if (!active) return;
    this.#activeTabId = active.id;

    const { tablist } = this.refs;
    if (tablist) {
      const owns = tabs.map((tab) => tab.id).join(' ');
      setIfChanged(tablist, 'aria-owns', owns);
    }

    for (const tab of tabs) {
      const selected = tab === active;
      setIfChanged(tab, 'aria-selected', selected ? 'true' : 'false');
      setIfChanged(tab, 'tabindex', selected ? '0' : '-1');
    }

    const activePanelId = active.getAttribute('aria-controls');
    for (const panel of this.#panels) {
      const hidden = panel.id !== activePanelId;
      if (panel.hidden !== hidden) panel.hidden = hidden;
    }

    if (this.getAttribute('data-initialized') !== 'true') {
      this.setAttribute('data-initialized', 'true');
    }
  }
}

/**
 * Sets an attribute only when the value differs, so the component's own
 * writes don't retrigger its MutationObserver.
 *
 * @param {Element} element
 * @param {string} name
 * @param {string} value
 */
function setIfChanged(element, name, value) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
